import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Home, MapPin, Maximize, Banknote, User, Grid3x3, Kanban } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { properties, agents, type Property, type Agent } from '@/lib/api';
import { formatCurrency, statusLabels, statusColors, propertyTypes } from '@/lib/utils';
import { PropertyEditor } from '@/components/properties/PropertyEditor';
import { KanbanBoard } from '@/components/kanban';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Properties() {
  const { token, user } = useAuthStore();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState<'grid' | 'kanban'>('grid');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  // Load view preference from localStorage
  useEffect(() => {
    const savedView = localStorage.getItem('properties-view') as 'grid' | 'kanban' | null;
    if (savedView) {
      setView(savedView);
    }
  }, []);

  // Save view preference to localStorage
  const handleViewChange = (newView: string) => {
    const viewValue = newView as 'grid' | 'kanban';
    setView(viewValue);
    localStorage.setItem('properties-view', viewValue);
  };

  const handlePropertyClick = (property: Property) => {
    setSelectedProperty(property);
    setIsDetailModalOpen(true);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['properties', statusFilter],
    queryFn: () => properties.list(token!, { status: statusFilter || undefined, limit: 50 }),
    enabled: !!token,
  });

  // Fetch active agents for dropdown
  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agents.list(token!, false), // false = only active agents
    enabled: !!token,
  });

  // Mutation for assigning agent to property
  const assignAgentMutation = useMutation({
    mutationFn: ({ propertyId, agentId }: { propertyId: string; agentId: string | null }) =>
      properties.update(token!, propertyId, { agent_id: agentId || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });

  // Handle agent assignment change
  const handleAgentChange = (e: React.ChangeEvent<HTMLSelectElement>, propertyId: string) => {
    e.stopPropagation(); // Prevent card click
    const agentId = e.target.value || null;
    assignAgentMutation.mutate({ propertyId, agentId });
  };

  // Get agent name by ID
  const getAgentName = (agentId?: string): string | null => {
    if (!agentId || !agentsData?.data) return null;
    const agent = agentsData.data.find((a: Agent) => a.id === agentId);
    return agent?.name || null;
  };

  const filteredProperties = data?.data.filter((prop) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      prop.address.toLowerCase().includes(searchLower) ||
      prop.city?.toLowerCase().includes(searchLower) ||
      prop.property_type.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Eignir</h1>
          <p className="text-gray-600 mt-1">
            {data?.pagination.total || 0} eignir samtals
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <Tabs value={view} onValueChange={handleViewChange} className="w-auto">
            <TabsList>
              <TabsTrigger value="grid" className="flex items-center gap-2">
                <Grid3x3 className="w-4 h-4" />
                <span className="hidden sm:inline">Yfirlit</span>
              </TabsTrigger>
              <TabsTrigger value="kanban" className="flex items-center gap-2">
                <Kanban className="w-4 h-4" />
                <span className="hidden sm:inline">Kanban</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ný eign
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Leita að heimilisfangi, borg..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-auto"
          >
            <option value="">Allar stöður</option>
            <option value="á_markaði">Á markaði</option>
            <option value="selt">Selt</option>
            <option value="frátekið">Frátekið</option>
            <option value="afskráð">Afskráð</option>
          </select>
        </div>
      </div>

      {/* Content - Grid or Kanban View */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
        </div>
      ) : filteredProperties?.length === 0 ? (
        <div className="card p-12 text-center">
          <Home className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Engar eignir fundust</p>
        </div>
      ) : (
        <div className="transition-opacity duration-200">
          {view === 'kanban' ? (
            // Kanban View
            <KanbanBoard agencyId={user?.agency_id || ''} />
          ) : (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties?.map((prop) => (
                <div
                  key={prop.id}
                  className="card overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handlePropertyClick(prop)}
                >
                  {/* Image or placeholder */}
                  <div className="h-48 bg-gray-200 flex items-center justify-center overflow-hidden">
                    {prop.images && prop.images.length > 0 ? (
                      <img
                        src={properties.getImageUrl(prop.id, prop.images[0])}
                        alt={prop.address}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Home className="w-12 h-12 text-gray-400" />
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-1">
                        {prop.address}
                      </h3>
                      <span className={statusColors[prop.status] || 'badge-gray'}>
                        {statusLabels[prop.status] || prop.status}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                      <MapPin className="w-4 h-4" />
                      {prop.postal_code} {prop.city}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <Home className="w-4 h-4" />
                        {propertyTypes[prop.property_type] || prop.property_type}
                      </span>
                      {prop.size_sqm && (
                        <span className="flex items-center gap-1">
                          <Maximize className="w-4 h-4" />
                          {prop.size_sqm} m²
                        </span>
                      )}
                      {prop.rooms && (
                        <span>{prop.rooms} herb.</span>
                      )}
                    </div>

                    {prop.price && (
                      <div className="flex items-center gap-1 text-lg font-semibold text-prima-600">
                        <Banknote className="w-5 h-5" />
                        {formatCurrency(prop.price)}
                      </div>
                    )}

                    {/* Agent Assignment */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <label className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <User className="w-4 h-4" />
                        Söluaðili
                      </label>
                      <select
                        value={prop.agent_id || ''}
                        onChange={(e) => handleAgentChange(e, prop.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="input text-sm w-full"
                        disabled={assignAgentMutation.isPending}
                      >
                        <option value="">Velja söluaðila</option>
                        {agentsData?.data?.map((agent: Agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name}
                          </option>
                        ))}
                      </select>
                      {prop.agent_id && getAgentName(prop.agent_id) && (
                        <p className="text-xs text-gray-500 mt-1">
                          Núverandi: {getAgentName(prop.agent_id)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Property Editor */}
      <PropertyEditor
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        property={null}
        mode="create"
      />

      {/* Edit Property Editor */}
      <PropertyEditor
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        property={selectedProperty}
        mode="edit"
      />
    </div>
  );
}
